import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";

import {
  fetchProducts,
  fetchCategories,
  fetchSubCategories,
  fetchChildCategories,
  fetchBrands
} from "../../core/api";

import api from "../../core/api/api";

const AdminCatalogContext = createContext(null);

/* 🔥 Safe response normalizer */
const normalize = res =>
  Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res?.data)
    ? res.data
    : [];


export function AdminCatalogProvider({ children }) {

  /* ================= FLAGS ================= */
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /* ================= MASTER DATA ================= */
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  /* ================= PRODUCTS ================= */
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState("");
const [productsMeta, setProductsMeta] = useState({
  total: 0,
  page: 1,
  totalPages: 1
});

  /* ================= INVENTORY (ADMIN ONLY) ================= */
  const [variantInventory, setVariantInventory] = useState([]);

  /* ================= LOAD MASTER ================= */

const loadCatalog = useCallback(async () => {
  try {
    setIsRefreshing(true);

    const [catRes, subRes, childRes, brandRes] = await Promise.all([
      fetchCategories(),
      fetchSubCategories(),
      fetchChildCategories(),
      fetchBrands()
    ]);

    setCategories(normalize(catRes));
    setSubCategories(normalize(subRes));
    setChildCategories(normalize(childRes));
    setBrands(normalize(brandRes));

    setIsInitialLoad(false);
    setIsRefreshing(false);
  } catch (err) {
    console.error("Admin catalog load failed", err);
    setIsRefreshing(false);
  }
}, []);   // 🔥 IMPORTANT

  /* ================= LOAD PRODUCTS ================= */

const loadProducts = useCallback(async ({
  page = 1,
  limit = 20,
  search,
  brand,
  category,
  subCategory,
  childCategory,
  stock,
  sort
} = {}) => {

  setProductsLoading(true);
  setProductsError("");

  try {
    const res = await fetchProducts({
      page,
      limit,
      search,
      brand,
      category,
      subCategory,
      childCategory,
      stock,
      sort,
      admin: 1        // include seller name (shopName) in the admin catalog
    });

    const normalized = normalize(res);

    setProducts(normalized);
    // res.data = { success, data:[...], pagination:{page,limit,total,totalPages} }
    setProductsMeta(res?.data?.pagination || res?.data || {});
  } catch (err) {
    setProducts([]);
    setProductsError(err.message || "Failed loading products");
  } finally {
    setProductsLoading(false);
  }

}, []);   // 🔥 IMPORTANT

  /* ================= LOAD INVENTORY (ADMIN API) ================= */

const loadInventory = useCallback(async () => {
  try {
    const res = await api.get("/inventory");
    setVariantInventory(normalize(res));
  } catch {
    setVariantInventory([]);
  }
}, []);   // 🔥 IMPORTANT



  /* ================= INITIAL ================= */

useEffect(() => {
  loadCatalog();
  loadInventory();
  loadProducts({ page: 1, limit: 500 }); // load all products for inventory joins
}, [loadCatalog, loadInventory, loadProducts]);

  /* ================= CATEGORY TREE ================= */

  const categoryTree = useMemo(() => {
    return categories.map(cat => ({
      ...cat,
      children: subCategories
        .filter(sub => String(sub.categoryId) === String(cat._id))
        .map(sub => ({
          ...sub,
          children: childCategories.filter(
            child => String(child.subCategoryId) === String(sub._id)
          )
        }))
    }));
  }, [categories, subCategories, childCategories]);

  /* ================= ADMIN PRODUCTS (RAW) ================= */

const adminProducts = useMemo(() => {
  return (products || []).map(p => ({
    ...p,
    id: p._id,
    hasVariants: Boolean(p.hasVariants) // 🔥 FORCE BOOLEAN
  }));
}, [products]);

  /* ================= CREATE PRODUCT ================= */

const createProduct = async payload => {
  try {
    const res = await api.post("/products", payload);

    console.log("CREATE PRODUCT RESPONSE:", res.data);

    // Try every possible response format safely
    const product =
      res?.data?.data ||
      res?.data?.product ||
      (res?.data?._id ? res.data : null);

    if (!product) {
      console.warn("Unexpected response format:", res.data);
      throw new Error("Invalid server response");
    }

    setProducts(prev => [product, ...prev]);

    return product;

  } catch (err) {
    console.error("createProduct failed:", err);
    throw err;
  }
};


  /* ================= CONTEXT ================= */

  return (
    <AdminCatalogContext.Provider
      value={{
        isInitialLoad,
        isRefreshing,

        categories,
        subCategories,
        childCategories,
        brands,
        categoryTree,

        products: adminProducts,
        productsMeta,
        productsLoading,
        productsError,

        variantInventory,
        inventory: variantInventory,

        reloadCatalog: loadCatalog,
        reloadProducts: loadProducts,
        reloadInventory: loadInventory,
        createProduct
      }}
    >
      {children}
    </AdminCatalogContext.Provider>
  );
}

export const useAdminCatalog = () => {
  const ctx = useContext(AdminCatalogContext);
  if (!ctx) {
    throw new Error("useAdminCatalog must be used inside AdminCatalogProvider");
  }
  return ctx;
};
